import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { PassportService } from '../../../../core/services/passport.service';
import { ModalDataService } from '../../../../core/services/modal-data.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../store/app.state';
import { AngularPhoneNumberInput } from 'angular-phone-number-input';
import { ModalService } from '../../../../core/services';

// Validateur personnalisé pour empêcher les espaces vides
function noWhitespaceValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (control.value && typeof control.value === 'string') {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }
  return null;
}

@Component({
  selector: 'app-add-contact-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AngularPhoneNumberInput,
  ],
  templateUrl: './add-contact-modal.component.html',
  styleUrls: ['./add-contact-modal.component.scss'],
})
export class AddContactModalComponent implements OnInit {
  form: FormGroup;
  loading: boolean = false;
  error: string | null = null;
  success: boolean = false;
  passportNumber: string = '';
  phoneNumberData: any = null; // Pour stocker les données du composant téléphone

  contactTypes = [
    { value: 'Père', label: 'Père' },
    { value: 'Mère', label: 'Mère' },
    { value: 'Conjoint(e)', label: 'Conjoint(e)' },
    { value: 'Enfant', label: 'Enfant' },
    { value: 'Frère/Sœur', label: 'Frère/Sœur' },
    { value: 'Grand-parent', label: 'Grand-parent' },
    { value: 'Oncle/Tante', label: 'Oncle/Tante' },
    { value: 'Cousin(e)', label: 'Cousin(e)' },
    { value: 'Ami(e)', label: 'Ami(e)' },
    { value: 'Voisin(e)', label: 'Voisin(e)' },
    { value: 'Collègue', label: 'Collègue' },
    { value: 'Tuteur légal', label: 'Tuteur légal' },
    { value: 'Autre', label: 'Autre' },
  ];

  constructor(
    private fb: FormBuilder,
    private passportService: PassportService,
    private modalService: ModalService,
    private store: Store<AppState>
  ) {
    this.form = this.fb.group({
      firstName: [
        '',
        [Validators.required, Validators.minLength(2), noWhitespaceValidator],
      ],
      lastName: [
        '',
        [Validators.required, Validators.minLength(2), noWhitespaceValidator],
      ],
      type: ['', Validators.required],
      mobileNumber: ['', Validators.required],
      address: [''],
      profession: [''],
      relationship: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    // Récupérer le passportNumber depuis le store
    this.store
      .select((state) => state.auth.currentUser)
      .subscribe({
        next: (currentUser) => {
          if (currentUser && currentUser.passportNumber) {
            this.passportNumber = currentUser.passportNumber;
          }
        },
      });

    // Écouter les changements du type de contact pour mettre à jour la relation
    this.form.get('type')?.valueChanges.subscribe((type) => {
      this.form.patchValue({ relationship: type });
    });
  }

  onSubmit(): void {
    if (this.form.valid && this.passportNumber) {
      this.loading = true;
      this.error = null;

      const formData = this.form.value;

      // Formater le numéro de téléphone au format international
      const formattedPhoneNumber = this.formatPhoneNumber(
        formData.mobileNumber
      );

      if (!formattedPhoneNumber) {
        this.error = 'Numéro de téléphone invalide';
        this.loading = false;
        return;
      }

      const contactData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        type: formData.type,
        phoneNumber: formattedPhoneNumber, // Utiliser le nom de champ attendu par l'API
        address: formData.address,
        profession: formData.profession,
        relationship: formData.relationship,
      };

      console.log('Sending contact data:', contactData); // Debug log

      this.passportService
        .addContact(this.passportNumber, contactData)
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.success = true;
            // Fermer le modal immédiatement après succès
            this.closeModal();
            // Émettre un événement pour informer que le contact a été ajouté
            window.dispatchEvent(new CustomEvent('contactAdded'));
          },
          error: (err) => {
            this.loading = false;
            this.error =
              "Erreur lors de l'ajout du contact. Veuillez réessayer.";
            console.error('Erreur ajout contact:', err);
          },
        });
    } else {
      this.markFormGroupTouched();
    }
  }

  onPhoneInputChange(event: any): void {
    // Stocker les données du composant téléphone
    this.phoneNumberData = event;
    console.log('Phone input data:', event); // Debug log
  }

  private formatPhoneNumber(phoneData: any): string | null {
    // Si c'est déjà une chaîne au format international, la retourner
    if (typeof phoneData === 'string' && phoneData.startsWith('+')) {
      return phoneData;
    }

    // Si c'est un objet du composant angular-phone-number-input
    if (phoneData && typeof phoneData === 'object') {
      // Essayer différents formats selon la structure de l'objet
      if (phoneData.internationalNumber) {
        return phoneData.internationalNumber;
      }
      if (phoneData.number && phoneData.countryCode) {
        return `+${phoneData.countryCode}${phoneData.number}`;
      }
      if (phoneData.phoneNumber) {
        return phoneData.phoneNumber;
      }
    }

    // Si c'est un objet avec countryCode et number séparés
    if (phoneData && phoneData.countryCode && phoneData.number) {
      return `+${phoneData.countryCode}${phoneData.number}`;
    }

    console.error('Format de numéro de téléphone non reconnu:', phoneData);
    return null;
  }

  markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  closeModal(): void {
    // Émettre un événement pour fermer le modal
    this.modalService.closeAll();
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Ce champ est requis';
      }
      if (control.errors['minlength']) {
        return `Minimum ${control.errors['minlength'].requiredLength} caractères`;
      }
      if (control.errors['whitespace']) {
        return 'Ce champ ne peut pas contenir uniquement des espaces';
      }
    }
    return '';
  }
}
